// src/pages/Inventory/GoodsReceiptNoteIns_AddListing.jsx
// ✅ Converted from: GoodsReceiptNoteIns_AddListing.aspx / .aspx.cs
// ✅ Uses Node routes you created: /api/inventory/grn-ins-add-listing/*
// ✅ NO rights logic, NO customization-flag switching
// ✅ Includes pagination, single-selection validation ("Please select any One")
// ✅ Keeps HO/BRANCH radio behavior and date validations (future date, from<=to)

import { useEffect, useContext, useMemo, useState } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import LayoutWrapper from "../../components/LayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  Building2,
  Search,
  Save,
  RefreshCcw,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

const BASE_URL = "https://nissi-india.com"; // change if needed

const GoodsReceiptNoteInsAddListing = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  const finYearId = localStorage.getItem("finYearId") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branchType = localStorage.getItem("branchType") || "HO";

  const screenBadge = useMemo(
    () => "INFRA SCREEN : INVENTORY - GRN INSPECTION ADD LISTING",
    []
  );

  const inputBase =
    `w-full px-4 py-3 rounded-2xl border outline-none transition ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const labelBase = `text-sm font-semibold ${
    isDark ? "text-gray-200" : "text-gray-700"
  }`;

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");

  // UI state
  const [showFilters, setShowFilters] = useState(true);
  const [loading, setLoading] = useState(false);

  // meta/defaults
  const [defaultFromDate, setDefaultFromDate] = useState("");
  const [defaultToDate, setDefaultToDate] = useState("");

  // radio (ASP: HO checked if session branchtype HO, else BRANCH)
  const [rad, setRad] = useState(branchType === "HO" ? "HO" : "BRANCH");
  const purType = rad === "HO" ? "H" : "B"; // ASP rad_checked
  const mode = "GRN"; // This screen is GRN Inspection Add Listing => GRN mode

  // filters (ASP controls)
  const [supplierId, setSupplierId] = useState("Select");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [projectId, setProjectId] = useState("Select");
  const [projectCodeId, setProjectCodeId] = useState("Select");
  const [storeId, setStoreId] = useState("Select");
  const [grnNo, setGrnNo] = useState(""); // txt_grn_no in ASP

  // dropdown data
  const [suppliers, setSuppliers] = useState([]);
  const [projectsName, setProjectsName] = useState([]);
  const [projectsCode, setProjectsCode] = useState([]);
  const [stores, setStores] = useState([]);

  // list
  const [rows, setRows] = useState([]);

  // selection: ASP requires exactly 1
  const [selectedId, setSelectedId] = useState(null);

  // pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const saveBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ` +
    (!selectedId ? "bg-gray-400 cursor-not-allowed" : "bg-emerald-600 hover:bg-emerald-700");

  const showingFrom = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingTo = Math.min(page * pageSize, totalCount);

  // ---------------- validations (ASP Check_from_to equivalent) ----------------
  const mustHaveFinYearId = () => {
    const id = Number(finYearId);
    if (!id || !Number.isFinite(id)) {
      toast.warn("Please select Financial Year.");
      return false;
    }
    return true;
  };

  const validateDates = () => {
    if (fromDate && toDate && toDate < fromDate) {
      toast.warn("Please select from date less than to date");
      return false;
    }
    const todayISO = new Date().toISOString().slice(0, 10);
    if (fromDate && fromDate > todayISO) {
      toast.warn("Select from date lesser than current date");
      return false;
    }
    if (toDate && toDate > todayISO) {
      toast.warn("Select to date lesser than current date");
      return false;
    }
    return true;
  };

  // ASP did NOT strictly require supplier for GRN mode, but your template enforces it.
  // To avoid breaking legacy behavior, we will NOT require supplier; it can be Select => backend uses 0.
  const normalizeSupplierId = () => {
    if (!supplierId || supplierId === "Select") return 0;
    return Number(supplierId);
  };

  // ---------------- API calls ----------------
  const fetchMeta = async () => {
    if (!branchId || !branchType) {
      toast.error("Missing branchId / branchType in localStorage");
      return;
    }
    if (!mustHaveFinYearId()) return;

    try {
      const res = await axios.get(
        `${BASE_URL}/api/inventory/grn-ins-add-listing/meta`,
        {
          params: {
            finYearId: Number(finYearId),
            branchId: Number(branchId),
            branchType,
          },
        }
      );

      if (res?.data?.success) {
        setDefaultFromDate(res.data.defaultFromDate || "");
        setDefaultToDate(res.data.defaultToDate || "");
        setFromDate(res.data.defaultFromDate || "");
        setToDate(res.data.defaultToDate || "");
      } else {
        toast.error(res?.data?.message || "Failed to load meta");
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err?.message || "Meta error"
      );
    }
  };

  const fetchDropdowns = async () => {
    if (!branchId || !branchType) return;
    if (!mustHaveFinYearId()) return;

    try {
      const res = await axios.get(
        `${BASE_URL}/api/inventory/grn-ins-add-listing/dropdowns`,
        {
          params: {
            finYearId: Number(finYearId),
            branchId: Number(branchId),
            branchType,
            purType,
          },
        }
      );

      if (res?.data?.success) {
        setSuppliers(res.data.suppliers || []);
        setStores(res.data.stores || []);
        setProjectsName(res.data.projectsName || []);
        setProjectsCode(res.data.projectsCode || []);
      } else {
        toast.error(res?.data?.message || "Failed to load dropdowns");
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err?.message || "Dropdown load error"
      );
    }
  };

  const fetchList = async (p = page) => {
    if (!branchId || !branchType) {
      toast.error("Missing branchId / branchType");
      return;
    }
    if (!mustHaveFinYearId()) return;
    if (!validateDates()) return;

    try {
      setLoading(true);

      const res = await axios.get(
        `${BASE_URL}/api/inventory/grn-ins-add-listing/list`,
        {
          params: {
            finYearId: Number(finYearId),
            branchId: Number(branchId),
            branchType,
            purType,
            mode, // "GRN"

            supplierId: normalizeSupplierId(),
            grnNo: grnNo.trim(),

            fromDate: fromDate || "",
            toDate: toDate || "",

            storeId: storeId !== "Select" ? storeId : "Select",
            projectId: projectCodeId !== "Select" ? projectCodeId : "Select", // ASP merge used projectcode dropdown value

            page: p,
            pageSize,
          },
        }
      );

      if (res?.data?.success) {
        setRows(res.data.items || []);
        setTotalCount(res.data.totalCount || 0);
        setTotalPages(res.data.totalPages || 1);
        setPage(res.data.page || p);

        setSelectedId(null);
      } else {
        toast.error(res?.data?.message || "Search failed");
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err?.message || "Search error"
      );
    } finally {
      setLoading(false);
    }
  };

  // ---------------- selection (ConfirmSubmit: exactly one) ----------------
  const toggleSelectSingle = (grnId) => {
    setSelectedId((prev) => (prev === grnId ? null : grnId));
  };

  // ---------------- actions ----------------
  const handleSearch = async (e) => {
    e?.preventDefault?.();
    setPage(1);
    await fetchList(1);
  };

  const handleRefresh = async () => {
    setSupplierId("Select");
    setProjectId("Select");
    setProjectCodeId("Select");
    setStoreId("Select");
    setGrnNo("");

    setFromDate(defaultFromDate || "");
    setToDate(defaultToDate || "");

    setRows([]);
    setSelectedId(null);
    setPage(1);
    setTotalCount(0);
    setTotalPages(1);

    await fetchDropdowns();
    toast.info("Reset done");
  };

  const handleSave = async () => {
    if (!mustHaveFinYearId()) return;

    if (!selectedId) {
      toast.warn("Please select any One");
      return;
    }

    try {
      setLoading(true);

      // ASP SAVE Mode A:
      // Purchaseorder_dt = GetPurchaseOrders(grnid)
      // Session["grnins_purchaseorder"] = dt
      // redirect to GoodsReceiptNoteIns_EntryGST.aspx
      const res = await axios.get(
        `${BASE_URL}/api/inventory/grn-ins-add-listing/grn/${Number(
          selectedId
        )}/purchase-orders`,
        {
          params: { finYearId: Number(finYearId) },
        }
      );

      if (res?.data?.success) {
        sessionStorage.setItem(
          "grnInsSelectionPayload",
          JSON.stringify({
            grnId: Number(selectedId),
            grn_type: rad === "HO" ? "HO" : "BRANCH",
            grnins_purchaseorder: res.data.grnins_purchaseorder || [],
          })
        );

        toast.success("Saved selection");
        // match ASP redirect:
        window.location.href = `/Inventory/GoodsReceiptNote_Ins_EntryGST`;
      } else {
        toast.error(res?.data?.message || "Save failed");
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err?.message || "Save error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleProjectCodeChange = (val) => {
    setProjectCodeId(val);
    setProjectId(val);
  };

  const handleProjectNameChange = (val) => {
    setProjectId(val);
    setProjectCodeId(val);
  };

  // ---------------- lifecycle ----------------
  useEffect(() => {
    fetchMeta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchDropdowns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purType]);

  useEffect(() => {
    // keep legacy: don't auto-search on supplier change; user clicks Search.
    // But if you want auto-search, uncomment:
    // if (supplierId !== "Select") fetchList(1);
  }, [supplierId]); // eslint-disable-line

  useEffect(() => {
    if (page > 1) fetchList(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  return (
    <LayoutWrapper>
      <ToastContainer
        position="top-right"
        autoClose={2800}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">GRN INSPECTION ADD LISTING</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* Top row */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow ${isDark ? "bg-white/10" : "bg-gray-100"}`}>
                <Building2 className={isDark ? "text-white" : "text-gray-700"} />
              </div>

              <div>
                <div className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>
                  GRN(s) for Inspection
                </div>
                <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Supplier / Date / Project / Store / GRN No + Pagination + Select & Save
                </div>
              </div>

              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-blue-900/40 text-blue-100" : "bg-blue-100 text-blue-800"}`}>
                {screenBadge}
              </div>

              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-emerald-900/40 text-emerald-100" : "bg-emerald-100 text-emerald-800"}`}>
                {totalCount} Records
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 justify-end">
              <button onClick={handleRefresh} className={ghostBtn} disabled={loading}>
                <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                Reset
              </button>

              <button onClick={() => setShowFilters((v) => !v)} className={ghostBtn}>
                {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button onClick={handleSave} disabled={loading || !selectedId} className={saveBtn}>
                <Save className="w-4 h-4" />
                Save
              </button>
            </div>
          </div>

          {/* Filters */}
          {showFilters && (
            <form onSubmit={handleSearch} className={cardBase + " p-6 mb-6"}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className={labelBase}>Branch</label>
                  <div className="mt-2 flex items-center gap-6">
                    <label className="inline-flex items-center gap-2">
                      <input
                        type="radio"
                        name="branch"
                        checked={rad === "HO"}
                        onChange={() => setRad("HO")}
                        disabled={branchType === "HO"} // ASP disabled BRANCH radio when session branchtype HO; here keep HO locked
                      />
                      <span className={isDark ? "text-gray-200" : "text-gray-700"}>H.O.</span>
                    </label>

                    <label className="inline-flex items-center gap-2">
                      <input
                        type="radio"
                        name="branch"
                        checked={rad === "BRANCH"}
                        onChange={() => setRad("BRANCH")}
                      />
                      <span className={isDark ? "text-gray-200" : "text-gray-700"}>BRANCH</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className={labelBase}>Supplier</label>
                  <select value={supplierId} onChange={(e) => setSupplierId(e.target.value)} className={inputBase}>
                    <option value="Select">Select</option>
                    {suppliers.map((x) => (
                      <option key={x.supplierId} value={x.supplierId}>
                        {x.supplierName}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelBase}>From Date</label>
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className={inputBase}
                  />
                </div>

                <div>
                  <label className={labelBase}>To Date</label>
                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className={inputBase}
                  />
                </div>

                <div>
                  <label className={labelBase}>GRN No</label>
                  <input
                    value={grnNo}
                    onChange={(e) => setGrnNo(e.target.value)}
                    placeholder="Type GRN No..."
                    className={inputBase}
                  />
                </div>

                <div>
                  <label className={labelBase}>Store</label>
                  <select value={storeId} onChange={(e) => setStoreId(e.target.value)} className={inputBase}>
                    <option value="Select">Select</option>
                    {stores.map((st) => (
                      <option key={st.storeId} value={st.storeId}>
                        {st.storeName}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelBase}>Project Code</label>
                  <select
                    value={projectCodeId}
                    onChange={(e) => handleProjectCodeChange(e.target.value)}
                    className={inputBase}
                  >
                    <option value="Select">Select</option>
                    {projectsCode.map((p) => (
                      <option key={p.projectId} value={p.projectId}>
                        {p.projectCode}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelBase}>Project Name</label>
                  <select
                    value={projectId}
                    onChange={(e) => handleProjectNameChange(e.target.value)}
                    className={inputBase}
                  >
                    <option value="Select">Select</option>
                    {projectsName.map((p) => (
                      <option key={p.projectId} value={p.projectId}>
                        {p.projectName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mt-6 flex justify-center">
                <button type="submit" className={primaryBtn} disabled={loading}>
                  <Search className="w-4 h-4" />
                  Search
                </button>
              </div>
            </form>
          )}

          {/* Table */}
          <div className={cardBase}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${primaryBtnClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-bold">Slno</th>
                    <th className="px-4 py-4 text-left font-bold">GRN No.</th>
                    <th className="px-4 py-4 text-left font-bold">Date</th>
                    <th className="px-4 py-4 text-left font-bold">Supplier</th>
                    <th className="px-4 py-4 text-left font-bold">Status</th>
                    <th className="px-4 py-4 text-center font-bold">Check</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    Array.from({ length: pageSize }).map((_, idx) => (
                      <tr key={idx} className={isDark ? "bg-gray-900" : "bg-white"}>
                        {Array.from({ length: 6 }).map((__, c) => (
                          <td key={c} className="px-4 py-4">
                            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={6} className={`px-4 py-12 text-center ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => (
                      <tr
                        key={r.fld_grnid}
                        className={`${isDark ? "bg-gray-900" : "bg-white"} hover:${isDark ? "bg-gray-800" : "bg-gray-50"}`}
                      >
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {(page - 1) * pageSize + idx + 1}
                        </td>
                        <td className={`px-4 py-4 text-sm text-left ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_grnno}
                        </td>
                        <td className={`px-4 py-4 text-sm text-left ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_date}
                        </td>
                        <td className={`px-4 py-4 text-sm text-left ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_suppliername}
                        </td>
                        <td className={`px-4 py-4 text-sm text-left ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.status}
                        </td>
                        <td className="px-4 py-4 text-center">
                          <input
                            type="checkbox"
                            checked={selectedId === r.fld_grnid}
                            onChange={() => toggleSelectSingle(Number(r.fld_grnid))}
                            className="w-4 h-4"
                          />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="flex flex-col md:flex-row items-center justify-between mt-6 gap-4">
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              Showing <b>{showingFrom}</b> to <b>{showingTo}</b> of <b>{totalCount}</b>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {totalPages > 1 && (
                <div className="inline-flex rounded-2xl overflow-hidden border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className={`px-4 py-2 text-sm font-bold ${
                      page === 1
                        ? "text-gray-400 cursor-not-allowed"
                        : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                    }`}
                  >
                    Prev
                  </button>

                  {[...Array(totalPages)].map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setPage(i + 1)}
                      className={`px-4 py-2 text-sm font-bold transition ${
                        page === i + 1
                          ? `${primaryBtnClass} text-white`
                          : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}

                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className={`px-4 py-2 text-sm font-bold ${
                      page === totalPages
                        ? "text-gray-400 cursor-not-allowed"
                        : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                    }`}
                  >
                    Next
                  </button>
                </div>
              )}

              <button onClick={() => fetchList(page)} className={ghostBtn} disabled={loading}>
                <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                Refresh List
              </button>
            </div>
          </div>
        </div>
      </div>
    </LayoutWrapper>
  );
};

export default GoodsReceiptNoteInsAddListing;